// src/data/rules.js
// Sources:
//   - LISD Student & Parent Handbook 2025-26
//   - HB 1481 (Texas state law on personal devices)
//   - LISD Student Code of Conduct
// Captured May 2026.

import { SOURCES } from './sources.js';

export const rules = [
  {
    id: 'phones-hb1481',
    title: 'No personal devices during school',
    shortTitle: 'Phone ban (HB 1481)',
    category: 'devices',
    severity: 'high',
    description:
      'Personal phones, smartwatches, earbuds, and tablets must be off, out of sight, and stored in a locker or backpack from first bell to last bell — including lunch and passing periods. This is a Texas state law (HB 1481), not a CRMS-specific rule.',
    consequences:
      'First offense: confiscation, returned to parent. Repeat offenses: discipline per Student Code of Conduct.',
    exceptions: [
      'IEP/504 accommodations',
      'Documented medical needs',
      'Safety/health requirements imposed by law',
    ],
    source: SOURCES.hb1481,
  },
  {
    id: 'tardies-10-min',
    title: 'Tardies and the 10-minute rule',
    shortTitle: 'Tardy / Absent rule',
    category: 'attendance',
    severity: 'medium',
    description:
      'A student arriving within the first 10 minutes after class begins is marked tardy. A student arriving more than 10 minutes late is marked absent for that period. (Texas Education Code §25.092 requires 90% attendance for class credit.)',
    consequences:
      'Repeated tardiness can affect class credit. Absences accumulate toward the 90% attendance requirement.',
    exceptions: [
      'Excused absences (illness, doctor appointment with note, family emergency, religious observance)',
    ],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'attendance-90-percent',
    title: '90% attendance for class credit',
    shortTitle: 'Attendance for credit',
    category: 'attendance',
    severity: 'high',
    description:
      'Per Texas Education Code §25.092, students must attend at least 90% of the days a class is offered to receive credit. Students attending 75-89% may receive credit with a principal-approved plan.',
    consequences:
      'Below 75% attendance generally results in loss of credit unless an extenuating-circumstances appeal is granted.',
    exceptions: [
      'Serious or life-threatening illness (with district-adopted physician form)',
      'Documented religious holy day observance',
      'State-approved non-absences (military deployment, court appearance, etc.)',
    ],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'dress-code',
    title: 'Dress code',
    shortTitle: 'Dress code',
    category: 'dress',
    severity: 'medium',
    description:
      'LISD has a district-wide dress code covering shirts, shorts, pants, footwear, and accessories. Specifics are detailed in the Student & Parent Handbook. The dress code applies to all school days and school-sponsored events.',
    consequences:
      'Students out of compliance are typically asked to change, borrow appropriate clothing from the office, or have a parent bring a replacement.',
    exceptions: [
      'Religious or cultural attire',
      'Documented medical accommodations',
      'Approved spirit days and themed dress days',
    ],
    source: SOURCES.studentHandbook,
    verificationNeeded: true,
    verificationNote:
      'Dress code specifics update annually. Refer to the latest Student & Parent Handbook for the current year.',
  },
  {
    id: 'academic-integrity',
    title: 'Academic integrity',
    shortTitle: 'No cheating or plagiarism',
    category: 'integrity',
    severity: 'high',
    description:
      "Cheating, plagiarism, copying another student's work, and using AI tools to complete assignments without permission all violate academic integrity. The 10 Ethical Principles include Honesty, Integrity, and Accountability — the academic integrity policy applies these to schoolwork.",
    consequences:
      'Loss of credit on the assignment, possible loss of credit for the grading period, parent notification, and discipline per Student Code of Conduct. Repeated violations can affect promotion and NJHS eligibility.',
    exceptions: [],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'bullying',
    title: 'Bullying is not tolerated',
    shortTitle: 'Anti-bullying',
    category: 'conduct',
    severity: 'high',
    description:
      'Texas law defines bullying as a single significant act or pattern of acts directed at another student that exploits a power imbalance. Bullying includes hazing, threats, taunting, teasing, confinement, assault, demands for money, destruction of property, theft, name-calling, rumor-spreading, and ostracism. Cyberbullying via phone, computer, email, social media, or messaging is also prohibited.',
    consequences:
      'Investigation by administration, parent notification (for both victim and accused student), and discipline per Student Code of Conduct.',
    exceptions: [],
    reportingOptions: [
      'Tell any teacher, counselor, principal, or district employee',
      'Anonymous Alerts: anonymousalerts.com/leanderisd (login: leanderisd / password: leanderisd)',
    ],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'harassment',
    title: 'Harassment is not tolerated',
    shortTitle: 'Anti-harassment',
    category: 'conduct',
    severity: 'high',
    description:
      'Harassment based on race, color, religion, national origin, sex, sexual orientation, or disability is prohibited. All students are expected to treat each other and district employees with courtesy and respect, and to stop offensive behavior when asked.',
    consequences:
      'Substantiated complaints result in disciplinary action per the Student Code of Conduct.',
    exceptions: [],
    reportingOptions: [
      'Any campus administrator or staff member',
      'Title IX coordinator: Bryan Miller, bryan.miller@leanderisd.org, 512-570-0150',
      'Office of Student Support Services: 512-570-0150',
    ],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'sexual-harassment',
    title: 'Sexual harassment is not tolerated',
    shortTitle: 'Sexual harassment',
    category: 'safety',
    severity: 'high',
    description:
      'Unwanted verbal or physical conduct of a sexual nature toward another student or district employee is prohibited. This includes words, gestures, requests for sexual favors, and any other sexual conduct.',
    consequences:
      'Investigation, parent notification (for non-minor allegations or any incident involving an employee), and discipline per Student Code of Conduct. Title IX procedures apply.',
    exceptions: [],
    reportingOptions: [
      'Principal or designee',
      'Title IX coordinator: Bryan Miller, bryan.miller@leanderisd.org, 512-570-0150',
    ],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'hall-passes',
    title: 'Hall passes required',
    shortTitle: 'Hall passes',
    category: 'conduct',
    severity: 'low',
    description:
      'All students must have a hall pass signed by an administrator or teacher to be in the halls before school, during class, or during lunch. While in the halls, voices should be kept low.',
    consequences: 'Repeated violations result in disciplinary action.',
    exceptions: [],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'discipline-ladder',
    title: 'How discipline works',
    shortTitle: 'Discipline alternatives',
    category: 'conduct',
    severity: 'medium',
    description:
      'For students who do not exercise self-discipline, school officials may use any of the following: community service, counseling, withdrawal of privileges, parent conference, detention, suspension, alternative education (LEO), expulsion, judicial proceeding, peer mediation, or individualized behavior plan.',
    consequences:
      'The specific consequence depends on the offense and the Student Code of Conduct.',
    exceptions: [],
    source: SOURCES.studentHandbook,
  },
  {
    id: 'ten-ethical-principles',
    title: 'The 10 Ethical Principles',
    shortTitle: '10 Ethical Principles',
    category: 'conduct',
    severity: 'low',
    description:
      "LISD's shared expectations for student, staff, and family conduct: (1) Honesty, (2) Integrity, (3) Promise-keeping, (4) Loyalty, (5) Concern for Others, (6) Law-abidance / Civic Duty, (7) Respect for Others, (8) Fairness, (9) Pursuit of Excellence, (10) Accountability. These are not enforceable rules with consequences; they're the framework that informs the Student Code of Conduct.",
    consequences: null,
    exceptions: [],
    source: SOURCES.studentHandbook,
  },
];

// ============================================================
// WHAT TO DO IF — quick-reference matrix
// (used by DayInLife.jsx via named import)
// ============================================================

export const whatToDoIf = [
  {
    id: 'forgot-lunch',
    situation: 'I forgot my lunch and my account is empty',
    response:
      "You can charge up to $16 (about 5 lunches) on your account. The school never takes a tray away. After that, you'll get a low-cost meal until the balance is paid.",
    contact: null,
    contactPhone: null,
    contactUrl: null,
    source: SOURCES.childNutrition,
  },
  {
    id: 'cant-open-locker',
    situation: "I can't open my locker",
    response:
      "Go to the front office. Don't be late to class — they'll write you a pass and help with the locker after.",
    contact: 'Front Office (512-570-3500)',
    contactPhone: '512-570-3500',
    contactUrl: null,
    source: SOURCES.studentHandbook,
  },
  {
    id: 'missed-bus',
    situation: 'I missed the bus',
    response:
      "Call a parent or guardian. If you can't reach them, the front office can help you make calls.",
    contact: 'Front Office (512-570-3500)',
    contactPhone: '512-570-3500',
    contactUrl: null,
    source: SOURCES.studentHandbook,
  },
  {
    id: 'sick-at-school',
    situation: 'I feel sick at school',
    response:
      "Tell your teacher and ask for a pass to the clinic. The nurse will check you and call home if needed.",
    contact: 'Clinic (512-570-3507)',
    contactPhone: '512-570-3507',
    contactUrl: null,
    source: SOURCES.studentHandbook,
  },
  {
    id: 'lost-chromebook',
    situation: 'I lost my Chromebook',
    response:
      "Tell your teacher or go to the front office. The mLISD program has a process for reporting lost or damaged devices. Don't wait — report it the same day.",
    contact: 'Front Office (512-570-3500)',
    contactPhone: '512-570-3500',
    contactUrl: 'https://leanderisd.incidentiq.com',
    source: SOURCES.mLisd,
  },
  {
    id: 'dont-know-where-class',
    situation: "I don't know where my class is",
    response:
      "Ask any adult — teacher, hallway monitor, or front office staff. Everyone expects this in the first weeks of school. You won't get in trouble for being late if you tell them why.",
    contact: null,
    contactPhone: null,
    contactUrl: null,
    source: SOURCES.studentHandbook,
  },
  {
    id: 'being-bullied',
    situation: 'Someone is bullying me or someone I know',
    response:
      'Tell any adult right away — a teacher, counselor, principal, or any district employee. You can also report anonymously at anonymousalerts.com/leanderisd (login: leanderisd / password: leanderisd). Reports are monitored 7 AM – 5 PM on school days.',
    contact: 'Any school adult or Anonymous Alerts',
    contactPhone: null,
    contactUrl: 'https://www.anonymousalerts.com/leanderisd/',
    source: SOURCES.anonymousAlerts,
  },
  {
    id: 'something-feels-wrong',
    situation: "Something feels really wrong and I don't know what to do",
    response:
      "Tell a trusted adult — counselor, teacher, parent, or principal. If you can't talk to someone in person, use Anonymous Alerts at anonymousalerts.com/leanderisd. The form is monitored 7 AM – 5 PM on school days.",
    contact: 'Counselor, any trusted adult, or Anonymous Alerts',
    contactPhone: null,
    contactUrl: 'https://www.anonymousalerts.com/leanderisd/',
    source: SOURCES.anonymousAlerts,
  },
];

export default { rules, whatToDoIf };
